/**
 * IdentityCard - Rich preview card for ARNs (Microsoft Teams-style)
 * 
 * SHARED COMPONENT - Used by all plugins displaying membership identities
 * 
 * Features:
 * - Hover preview with avatar, details, status (300ms delay like Teams)
 * - Cross-plugin navigation support
 * - Supports: Person, Group, Account, Agent, Service
 * - Theme-aware (CSS variables)
 * - WCAG 2.1 AA accessible
 * 
 * Usage:
 *   const { IdentityCard } = (globalThis as any).MembershipComponents || {};
 *   <IdentityCard 
 *     arn="auth:account:empowernow:test" 
 *     type="account"
 *     name="Test User"
 *     status="active"
 *   />
 * 
 * @see experience/plugins/people-finder/IDENTITY_CARD_USAGE.md
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
const { useState, useRef, useCallback, useEffect } = React;

export type IdentityType = 'person' | 'group' | 'account' | 'agent' | 'service';
export type IdentityStatus = 'active' | 'inactive' | 'orphaned' | 'pending' | 'suspended';

export interface IdentityCardProps {
  /** Canonical ARN (e.g., auth:account:empowernow:test) */
  arn: string;
  /** Identity type determines icon and navigation */
  type: IdentityType;
  /** Display name */
  name: string;
  /** Email address (for persons) */
  email?: string;
  /** Job title (for persons) */
  title?: string;
  /** Current status */
  status?: IdentityStatus;
  /** Avatar URL (optional, falls back to icon) */
  avatar?: string;
  /** Department (for persons) */
  department?: string;
  /** Member count (for groups) */
  memberCount?: number;
  /** Provider name (for accounts/services) */
  provider?: string;
  /** Description text */
  description?: string;
  /** Custom click handler (overrides default navigation) */
  onClick?: () => void;
  /** Show hover preview card (default: true) */
  showPreview?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Disable interactions (display only) */
  disabled?: boolean;
}

// Type icons using emoji (theme-independent)
const TYPE_ICONS: Record<IdentityType, string> = {
  person: '👤',
  group: '👥',
  account: '🔐',
  agent: '🤖',
  service: '⚙️',
};

// Status colors using CSS variables for theme compatibility
const STATUS_COLORS: Record<IdentityStatus, string> = {
  active: 'var(--success-color, #10b981)',
  inactive: 'var(--main-text-secondary, #6b7280)',
  orphaned: 'var(--warning-color, #f59e0b)',
  pending: 'var(--info-color, #3b82f6)',
  suspended: 'var(--error-color, #ef4444)',
};

// Size configuration
const SIZE_CONFIG = {
  small: { fontSize: '12px', padding: '4px 8px', gap: '6px', avatar: '20px', icon: '10px' },
  medium: { fontSize: '13px', padding: '6px 10px', gap: '8px', avatar: '24px', icon: '12px' },
  large: { fontSize: '14px', padding: '8px 14px', gap: '10px', avatar: '32px', icon: '14px' },
};

// Route mapping for cross-plugin navigation
const getIdentityRoute = (type: IdentityType, arn: string): string => {
  const encoded = encodeURIComponent(arn);
  switch (type) {
    case 'person': return `/identity-core/people/${encoded}`;
    case 'group': return `/identity-core/groups/${encoded}`;
    case 'account': return `/identity-core/accounts/${encoded}`;
    case 'agent': return `/agents/${encoded}`;
    case 'service': return `/identity-core/services/${encoded}`;
    default: return `/identity-core/${encoded}`;
  }
};

export function IdentityCard({
  arn,
  type,
  name,
  email,
  title,
  status = 'active',
  avatar,
  department,
  memberCount,
  provider,
  description,
  onClick,
  showPreview = true,
  size = 'medium',
  disabled = false,
}: IdentityCardProps) {
  const navigate = useNavigate();
  const [showCard, setShowCard] = useState(false);
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 });
  const badgeRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);

  const config = SIZE_CONFIG[size];
  const statusColor = STATUS_COLORS[status] || STATUS_COLORS.active;

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!showPreview || disabled) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    hoverTimeoutRef.current = window.setTimeout(() => {
      if (badgeRef.current) {
        const rect = badgeRef.current.getBoundingClientRect();
        const cardWidth = 340;
        const cardHeight = 320; // Slightly taller estimate
        
        // Smart positioning - center below badge, stay within viewport
        // Position slightly overlapping the badge to prevent "dead zone"
        let left = rect.left + (rect.width / 2) - (cardWidth / 2);
        let top = rect.bottom + 4; // Smaller gap for easier mouse movement
        
        // Keep within horizontal bounds
        if (left + cardWidth > window.innerWidth - 16) {
          left = window.innerWidth - cardWidth - 16;
        }
        if (left < 16) left = 16;
        
        // If not enough space below, show above
        if (top + cardHeight > window.innerHeight - 16) {
          top = rect.top - cardHeight - 4;
        }
        
        // Final bounds check for top
        if (top < 16) top = 16;
        
        setCardPosition({ top, left });
        setShowCard(true);
      }
    }, 300); // Teams-like 300ms delay
  }, [showPreview, disabled]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Longer delay to allow mouse to move to card
    hideTimeoutRef.current = window.setTimeout(() => {
      setShowCard(false);
    }, 300); // Increased from 150ms to 300ms
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    // Cancel any pending hide when mouse enters the card
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    // Small delay before hiding to allow re-entry
    hideTimeoutRef.current = window.setTimeout(() => {
      setShowCard(false);
    }, 100);
  }, []);

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (onClick) {
      onClick();
    } else {
      // Cross-plugin navigation - use React Router for in-tree navigation
      const route = getIdentityRoute(type, arn);
      navigate(route);
    }
  }, [onClick, type, arn, disabled, navigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  // Badge styles
  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: config.gap,
    padding: config.padding,
    background: 'color-mix(in srgb, var(--main-accent, #8b5cf6) 10%, transparent)',
    border: '1px solid var(--main-accent, #8b5cf6)',
    borderRadius: '20px',
    fontSize: config.fontSize,
    color: 'var(--main-text, #fff)',
    cursor: disabled ? 'default' : 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    opacity: disabled ? 0.6 : 1,
  };

  const avatarStyle: React.CSSProperties = {
    width: config.avatar,
    height: config.avatar,
    borderRadius: type === 'person' || type === 'agent' ? '50%' : '4px',
    background: `linear-gradient(135deg, ${statusColor}, color-mix(in srgb, ${statusColor} 70%, var(--main-accent, #8b5cf6)))`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: config.icon,
    flexShrink: 0,
    boxShadow: `0 2px 4px ${statusColor}40`,
  };

  const cardStyle: React.CSSProperties = {
    position: 'fixed',
    top: `${cardPosition.top}px`,
    left: `${cardPosition.left}px`,
    width: '340px',
    background: 'var(--modal-bg, var(--card-bg, #1e1e2e))',
    border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
    borderRadius: '12px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
    padding: '16px',
    paddingTop: '20px', // Extra padding top for invisible hover bridge
    marginTop: '-8px', // Overlap with badge area to prevent dead zone
    zIndex: 9999,
    animation: 'identityCardFadeIn 0.2s ease',
  };

  // Render badge (inline display)
  const renderBadge = () => (
    React.createElement('div', {
      ref: badgeRef,
      style: badgeStyle,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      title: arn,
      role: 'button',
      tabIndex: disabled ? -1 : 0,
      'aria-label': `${name}, ${type}, ${status}`,
      'aria-expanded': showCard,
      'aria-haspopup': showPreview ? 'dialog' : undefined,
    },
      // Avatar
      React.createElement('div', { style: avatarStyle, 'aria-hidden': 'true' },
        avatar
          ? React.createElement('img', { 
              src: avatar, 
              alt: '',
              style: { width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' } 
            })
          : TYPE_ICONS[type]
      ),
      // Name
      React.createElement('span', {
        style: {
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100px',
        }
      }, name),
      // Status dot
      React.createElement('div', {
        style: {
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: statusColor,
          flexShrink: 0,
          boxShadow: `0 0 4px ${statusColor}`,
        },
        'aria-label': status,
      })
    )
  );

  // Render preview card (popover)
  const renderPreviewCard = () => {
    if (!showCard || !showPreview) return null;

    return React.createElement('div', {
      style: cardStyle,
      onMouseEnter: handleCardMouseEnter,
      onMouseLeave: handleCardMouseLeave,
      role: 'dialog',
      'aria-label': `Details for ${name}`,
    },
      // Header with large avatar
      React.createElement('div', {
        style: { display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }
      },
        // Large avatar
        React.createElement('div', {
          style: {
            width: '56px',
            height: '56px',
            borderRadius: type === 'person' || type === 'agent' ? '50%' : '10px',
            background: `linear-gradient(135deg, ${statusColor}, color-mix(in srgb, ${statusColor} 60%, var(--main-accent, #8b5cf6)))`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            boxShadow: `0 4px 12px ${statusColor}40`,
            flexShrink: 0,
          }
        }, avatar 
          ? React.createElement('img', { src: avatar, alt: '', style: { width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' } }) 
          : TYPE_ICONS[type]
        ),
        // Name and title
        React.createElement('div', { style: { flex: 1, minWidth: 0 } },
          React.createElement('div', {
            style: { fontSize: '16px', fontWeight: 600, color: 'var(--main-text)', marginBottom: '2px' }
          }, name),
          title && React.createElement('div', {
            style: { fontSize: '13px', color: 'var(--main-text-secondary)', marginBottom: '4px' }
          }, title),
          // Status badge
          React.createElement('div', {
            style: {
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: `color-mix(in srgb, ${statusColor} 15%, transparent)`,
              border: `1px solid ${statusColor}`,
              borderRadius: '10px',
              fontSize: '11px',
              color: statusColor,
              fontWeight: 600,
              textTransform: 'uppercase',
            }
          },
            React.createElement('div', {
              style: { width: '5px', height: '5px', borderRadius: '50%', background: statusColor }
            }),
            status
          )
        )
      ),
      // Details section
      React.createElement('div', {
        style: {
          padding: '12px',
          background: 'var(--main-bg, rgba(0,0,0,0.2))',
          borderRadius: '8px',
          marginBottom: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }
      },
        email && React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', gap: '8px' }
        },
          React.createElement('span', { style: { fontSize: '14px', opacity: 0.8 } }, '✉️'),
          React.createElement('span', { style: { fontSize: '13px', color: 'var(--main-text)' } }, email)
        ),
        department && React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', gap: '8px' }
        },
          React.createElement('span', { style: { fontSize: '14px', opacity: 0.8 } }, '🏢'),
          React.createElement('span', { style: { fontSize: '13px', color: 'var(--main-text)' } }, department)
        ),
        provider && React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', gap: '8px' }
        },
          React.createElement('span', { style: { fontSize: '14px', opacity: 0.8 } }, '🔐'),
          React.createElement('span', { style: { fontSize: '13px', color: 'var(--main-text)' } }, `${provider} account`)
        ),
        memberCount !== undefined && React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', gap: '8px' }
        },
          React.createElement('span', { style: { fontSize: '14px', opacity: 0.8 } }, '👥'),
          React.createElement('span', { style: { fontSize: '13px', color: 'var(--main-text)' } }, `${memberCount} members`)
        ),
        description && React.createElement('div', {
          style: { fontSize: '12px', color: 'var(--main-text-secondary)', fontStyle: 'italic' }
        }, description),
        // ARN display
        React.createElement('div', {
          style: {
            fontSize: '11px',
            color: 'var(--main-text-secondary)',
            fontFamily: 'monospace',
            padding: '6px 8px',
            background: 'var(--card-bg, rgba(0,0,0,0.3))',
            borderRadius: '4px',
            wordBreak: 'break-all',
            marginTop: '4px',
          }
        }, arn)
      ),
      // Action button
      React.createElement('button', {
        style: {
          width: '100%',
          padding: '10px',
          background: 'var(--button-primary-bg, linear-gradient(135deg, var(--quantum-violet, #6C4CFF), var(--ultraviolet-magenta, #B326FF)))',
          border: 'none',
          borderRadius: '6px',
          color: '#fff',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        },
        onClick: handleClick,
        'aria-label': `View full details for ${name}`,
      }, '→ View Details')
    );
  };

  return React.createElement(React.Fragment, {},
    renderBadge(),
    showCard && renderPreviewCard()
  );
}

// Inject CSS animations (once per document)
if (typeof document !== 'undefined' && !document.getElementById('membership-identity-card-styles')) {
  const style = document.createElement('style');
  style.id = 'membership-identity-card-styles';
  style.textContent = `
    @keyframes identityCardFadeIn {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

export default IdentityCard;

